import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    TextField,
    InputAdornment,
    IconButton,
    Autocomplete,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
} from '@mui/material';
import {
    Search,
    Event as EventIcon,
    People,
    Restaurant,
    Inventory,
    Star,
    ChecklistRtl,
    Close,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getUserEvents } from '../../services/eventsService';
import { getUserRecipes } from '../../services/recipesService';
import { getUserGuests } from '../../services/guestsService';
import { useInventory } from '../../hooks/useInventory';
import { useWishlist } from '../../hooks/useWishlist';

interface SearchDialogProps {
    open: boolean;
    onClose: () => void;
}

export const SearchDialog: React.FC<SearchDialogProps> = ({ open, onClose }) => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();

    // We need to fetch these again since we can't easily pass them from MainLayout without prop drilling or context
    // Ideally, these would be in a global context or cached query, but for now we reuse the hooks/services
    const { items: inventoryItems } = useInventory();
    const { items: wishlistItems } = useWishlist();

    const [searchOptions, setSearchOptions] = useState<any[]>([]);
    const [loadingSearch, setLoadingSearch] = useState(false);

    useEffect(() => {
        if (open) {
            loadSearchOptions();
        }
    }, [open, user?.uid, inventoryItems, wishlistItems]);

    const loadSearchOptions = async () => {
        if (!user?.uid) return;

        setLoadingSearch(true);
        try {
            const options: any[] = [];

            // Páginas estáticas
            const pages = [
                { id: 'inventory-page', title: 'Acervo', type: 'page', path: '/inventory', icon: 'inventory' },
                { id: 'recipes-page', title: 'Receitas', type: 'page', path: '/recipes', icon: 'restaurant' },
                { id: 'guests-page', title: 'Convidados', type: 'page', path: '/guests', icon: 'person' },
                { id: 'calendar-page', title: 'Calendário', type: 'page', path: '/calendar', icon: 'event' },
                { id: 'todos-page', title: 'Tarefas', type: 'page', path: '/todo-list', icon: 'checklist' },
                { id: 'wishlist-page', title: 'Wishlist', type: 'page', path: '/wishlist', icon: 'star' },
                { id: 'events-page', title: 'Eventos', type: 'page', path: '/events', icon: 'event' },
            ];
            options.push(...pages);

            // Buscar eventos do serviço
            const events = await getUserEvents(user.uid);
            events.forEach(event => {
                options.push({
                    id: `event-${event.id}`,
                    title: event.title,
                    subtitle: new Date(event.date).toLocaleDateString('pt-BR'),
                    type: 'event',
                    path: `/events/${event.id}`,
                    icon: 'event',
                });
            });

            // Buscar convidados do serviço
            const guests = await getUserGuests(user.uid);
            guests.forEach(guest => {
                options.push({
                    id: `guest-${guest.id}`,
                    title: guest.name,
                    subtitle: guest.email || guest.phone,
                    type: 'guest',
                    path: `/guests/${guest.id}`,
                    icon: 'person',
                });
            });

            // Buscar receitas do serviço
            const recipes = await getUserRecipes(user.uid);
            recipes.forEach(recipe => {
                options.push({
                    id: `recipe-${recipe.id}`,
                    title: recipe.title,
                    subtitle: recipe.description,
                    type: 'recipe',
                    path: `/recipes/${recipe.id}`,
                    icon: 'restaurant',
                });
            });

            // Buscar inventário do hook
            inventoryItems.forEach(item => {
                options.push({
                    id: `inventory-${item.id}`,
                    title: item.itemName,
                    subtitle: `${item.category || ''} - ${item.brand || ''}`.trim(),
                    type: 'inventory',
                    path: `/inventory/${item.id}`,
                    icon: 'inventory',
                });
            });

            // Buscar wishlist do hook
            wishlistItems.forEach(item => {
                options.push({
                    id: `wishlist-${item.id}`,
                    title: item.title,
                    subtitle: item.store,
                    type: 'wishlist',
                    path: '/wishlist',
                    icon: 'star',
                });
            });

            setSearchOptions(options);
        } catch (error) {
            console.error('Erro ao carregar opções de busca:', error);
        } finally {
            setLoadingSearch(false);
        }
    };

    const getIcon = (iconName: string) => {
        const icons: any = {
            event: <EventIcon fontSize="small" />,
            person: <People fontSize="small" />,
            restaurant: <Restaurant fontSize="small" />,
            inventory: <Inventory fontSize="small" />,
            star: <Star fontSize="small" />,
            checklist: <ChecklistRtl fontSize="small" />,
        };
        return icons[iconName] || <Search fontSize="small" />;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    position: 'absolute',
                    top: 20,
                    borderRadius: theme.radii.large,
                    m: 2,
                    backgroundColor: theme.colors.background,
                }
            }}
        >
            <DialogContent sx={{ p: 2 }}>
                <Autocomplete
                    freeSolo
                    autoHighlight
                    openOnFocus
                    options={searchOptions}
                    loading={loadingSearch}
                    getOptionLabel={(option: any) => option.title || ''}
                    groupBy={(option: any) => {
                        const labels: any = {
                            page: 'Páginas',
                            event: 'Eventos',
                            guest: 'Convidados',
                            recipe: 'Receitas',
                            inventory: 'Acervo',
                            wishlist: 'Wishlist',
                        };
                        return labels[option.type] || 'Outros';
                    }}
                    onChange={(_, value: any) => {
                        if (value && value.path) {
                            navigate(value.path);
                            onClose();
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoFocus
                            placeholder="Buscar no Hostly..."
                            variant="outlined"
                            size="medium"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: theme.colors.primary }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {loadingSearch ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <IconButton onClick={onClose} size="small">
                                                <Close fontSize="small" />
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: theme.radii.pill,
                                    backgroundColor: theme.colors.card,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                    },
                                },
                            }}
                        />
                    )}
                    renderOption={(props, option: any) => (
                        <ListItem {...props} key={option.id}>
                            <ListItemIcon sx={{ minWidth: 40, color: theme.colors.primary }}>
                                {getIcon(option.icon)}
                            </ListItemIcon>
                            <ListItemText
                                primary={option.title}
                                secondary={option.subtitle}
                                primaryTypographyProps={{
                                    sx: { fontWeight: 500, color: theme.colors.text.primary },
                                }}
                                secondaryTypographyProps={{
                                    sx: {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        color: theme.colors.text.secondary,
                                    },
                                }}
                            />
                        </ListItem>
                    )}
                />
            </DialogContent>
        </Dialog>
    );
};
